import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import {
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  Clock3,
  CreditCard,
  ShieldCheck,
} from 'lucide-react';

import { useUser } from '../../contexts/UserContext';
import dayjs from '../../lib/day';
import PrivateLayout from '../layouts/PrivateLayout';
import Loading from '../common/Loading';
import {
  useFetchPaymentPlanSummary,
  useStartPaymentPlan,
  useUpdatePaymentPlanSetup,
} from '../../services/billing';
import PaymentPlanAuthorizationTerms, {
  buildPaymentPlanAuthorizationPayload,
  createPaymentPlanAuthorizationState,
  getPaymentPlanAuthorizationSignerName,
  isPaymentPlanAuthorizationComplete,
} from './PaymentPlanAuthorizationTerms';
import {
  getDefaultFirstInstallmentDate,
  getLatestFirstInstallmentDate,
} from './paymentPlanDateUtils';

const PRESET_TERMS = [3, 6, 9, 12];
const PRESET_DEPOSIT_PERCENTS = [10, 20, 25, 50];
const STEP_ORDER = ['today', 'schedule', 'review'];

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number(amount) || 0);

const formatCents = (cents) => formatCurrency((Number(cents) || 0) / 100);

const mutationErrorMessage = (err, fallback) =>
  err?.response?.data?.msg || err?.message || fallback;

const getPaymentPlanDownPayment = (plan) =>
  (plan?.installments || []).find(
    (installment) => Number(installment.sequence) === 0,
  ) || null;

const isPaymentPlanDepositProcessing = (plan) => {
  if (!plan) return false;
  const downPayment = getPaymentPlanDownPayment(plan);
  return (
    plan.metadata?.depositStatus === 'processing' ||
    downPayment?.status === 'processing'
  );
};

const openCheckoutUrl = (url) => {
  if (!url) return;
  // Same-tab navigation: window.open from an async mutation callback is
  // popup-blocked on iOS Safari, silently stranding the client before payment.
  window.location.assign(url);
};

const computeSchedule = ({
  balanceCents,
  termMonths,
  downPaymentCents,
  firstInstallmentDate,
  installmentDueDates,
}) => {
  if (!balanceCents || !termMonths) return null;
  const dp = clamp(Math.round(downPaymentCents || 0), 0, balanceCents);
  const remaining = balanceCents - dp;
  const base = Math.floor(remaining / termMonths);
  const rem = remaining % termMonths;
  const installmentCents = Array.from(
    { length: termMonths },
    (_, index) => base + (index < rem ? 1 : 0),
  );
  const firstDate =
    firstInstallmentDate && dayjs(firstInstallmentDate).isValid()
      ? dayjs(firstInstallmentDate)
      : dayjs(getDefaultFirstInstallmentDate());

  return {
    totalCents: balanceCents,
    downPaymentCents: dp,
    remainingCents: remaining,
    termMonths,
    installmentCents,
    installments: installmentCents.map((amountCents, index) => ({
      amountCents,
      dueDate:
        installmentDueDates?.[index] ||
        firstDate.add(index, 'month').format('YYYY-MM-DD'),
    })),
    monthlyTopCents: installmentCents[0] || 0,
  };
};

const PageShell = styled.div`
  min-height: calc(100vh - 120px);
  background: #f7f8fb;
  margin: -0.25rem -0.25rem 0;
  padding: 1.5rem;

  @media (max-width: 720px) {
    padding: 0.85rem;
  }
`;

const WizardFrame = styled.div`
  max-width: 1180px;
  margin: 0 auto;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  margin-bottom: 1.25rem;

  h1 {
    margin: 0;
    color: #0f1c33;
    font-size: 1.4rem;
    line-height: 1.2;
    font-weight: 700;
    letter-spacing: -0.015em;
  }

  p {
    margin: 0.3rem 0 0;
    color: #6b7689;
    max-width: 580px;
    line-height: 1.5;
    font-size: 0.92rem;
  }

  .back-btn {
    flex: 0 0 auto;
    border-radius: 8px;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
  }

  @media (max-width: 720px) {
    flex-direction: column-reverse;
    .back-btn {
      width: 100%;
      justify-content: center;
    }
  }
`;

const WizardGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 1rem;
  align-items: start;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const StepStack = styled.div`
  display: grid;
  gap: 1rem;
`;

const WizardSection = styled.section`
  border: 1px solid #e6ebf3;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(15, 28, 51, 0.04);
  overflow: hidden;
  scroll-margin-top: 18px;

  &[data-variant='today'] {
    border-color: #d8defe;
    background: linear-gradient(180deg, #f5f7ff 0%, #ffffff 60%);
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 1.25rem 0.7rem;
  border-bottom: 1px solid #eef2f7;

  .step-kicker {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    color: ${({ $variant }) => ($variant === 'today' ? '#4338ca' : '#6b7689')};
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 700;
  }

  .step-num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 999px;
    background: ${({ $variant, $completed }) => {
      if ($completed) return '#16a34a';
      return $variant === 'today' ? '#4f46e5' : '#e2e8f0';
    }};
    color: ${({ $variant, $completed }) => {
      if ($completed) return '#ffffff';
      return $variant === 'today' ? '#fff' : '#475569';
    }};
    font-size: 0.65rem;
    font-weight: 700;
    transition: background-color 0.2s ease;
  }

  h2 {
    margin: 0.5rem 0 0;
    color: #0f1c33;
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: -0.01em;
  }

  .section-aside {
    text-align: right;
    color: #0f1c33;
    font-weight: 700;
    font-size: 0.92rem;
    font-variant-numeric: tabular-nums;
  }

  .section-aside span {
    display: block;
    color: #7a8699;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 700;
    margin-bottom: 0.1rem;
  }
`;

const SectionBody = styled.div`
  padding: 1rem 1.25rem 1.15rem;
`;

const ControlGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 1rem 1.25rem;
`;

const ControlBlock = styled.div`
  .control-label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    margin-bottom: 0.4rem;
  }

  .label {
    color: #6b7689;
    font-size: 0.76rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 700;
  }

  .value {
    color: #0f1c33;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  input[type='range'] {
    width: 100%;
    accent-color: #4f46e5;
  }
`;

const InlineMoneyEditor = styled.label`
  display: inline-flex;
  align-items: baseline;
  gap: 0.15rem;
  color: #0f1c33;
  font-weight: 700;
  font-size: 1.65rem;
  line-height: 1.1;
  letter-spacing: -0.02em;

  input {
    width: 8.5rem;
    border: 0;
    border-bottom: 2px solid #c7d2fe;
    border-radius: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    outline: none;
    padding: 0.05rem 0;
  }

  input:focus {
    border-bottom-color: #4f46e5;
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.6rem;
`;

const Chip = styled.button`
  appearance: none;
  border: 1px solid ${({ $active }) => ($active ? '#4f46e5' : '#dde3ec')};
  background: ${({ $active }) => ($active ? '#eef2ff' : '#fff')};
  color: ${({ $active }) => ($active ? '#3730a3' : '#0f1c33')};
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  font-size: 0.85rem;
  border-radius: 999px;
  padding: 0.3rem 0.8rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #4f46e5;
    color: #3730a3;
  }
`;

const HintPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  margin-top: 0.75rem;
  padding: 0.4rem 0.65rem;
  border-radius: 8px;
  border: 1px solid
    ${({ $variant }) => ($variant === 'today' ? '#c7d2fe' : '#e6ebf3')};
  background: ${({ $variant }) =>
    $variant === 'today' ? '#eef2ff' : '#f7f8fb'};
  color: ${({ $variant }) => ($variant === 'today' ? '#3730a3' : '#475569')};
  font-size: 0.84rem;
  font-weight: 500;
`;

const SubmitRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1.1rem;

  .btn {
    display: inline-flex;
    align-items: center;
    border-radius: 8px;
    font-weight: 600;
    padding: 0.5rem 1.1rem;
  }
`;

const ManagerNoteBlock = styled.div`
  margin-top: 0.9rem;
  padding: 0.85rem 0.95rem;
  border: 1px solid #e6ebf3;
  border-radius: 12px;
  background: #f7f8fb;

  .note-label {
    color: #0f1c33;
    font-size: 0.86rem;
    font-weight: 700;
    margin-bottom: 0.15rem;
  }

  .note-hint {
    color: #6b7689;
    font-size: 0.8rem;
    line-height: 1.35;
    margin-bottom: 0.55rem;
  }
`;

const ScheduleList = styled.div`
  border: 1px solid #eef2f7;
  border-radius: 12px;
  overflow: hidden;
`;

const ScheduleHeader = styled.div`
  display: grid;
  grid-template-columns: 32px 1fr 140px 120px;
  gap: 0.75rem;
  align-items: center;
  padding: 0.55rem 0.95rem;
  background: #f7f8fb;
  color: #6b7689;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 700;

  .amount {
    text-align: right;
  }

  @media (max-width: 680px) {
    grid-template-columns: 32px 1fr 120px;
    .amount {
      display: none;
    }
  }
`;

const ScheduleRow = styled.div`
  display: grid;
  grid-template-columns: 32px 1fr 140px 120px;
  gap: 0.75rem;
  align-items: center;
  padding: 0.7rem 0.95rem;
  border-top: 1px solid #eef2f7;
  background: ${({ $isDeposit }) => ($isDeposit ? '#f5f7ff' : '#fff')};

  .num {
    width: 24px;
    height: 24px;
    border-radius: 999px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ $isDeposit }) => ($isDeposit ? '#4f46e5' : '#f1f5f9')};
    color: ${({ $isDeposit }) => ($isDeposit ? '#fff' : '#475569')};
    font-size: 0.76rem;
    font-weight: 700;
  }

  .label {
    color: ${({ $isDeposit }) => ($isDeposit ? '#3730a3' : '#0f1c33')};
    font-weight: 600;
  }

  .status {
    color: ${({ $isDeposit }) => ($isDeposit ? '#4338ca' : '#6b7689')};
    font-size: 0.78rem;
    font-weight: ${({ $isDeposit }) => ($isDeposit ? 600 : 400)};
    margin-top: 0.06rem;
  }

  .date {
    color: ${({ $isDeposit }) => ($isDeposit ? '#3730a3' : '#475569')};
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  .amount {
    color: ${({ $isDeposit }) => ($isDeposit ? '#3730a3' : '#0f1c33')};
    font-weight: 700;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  @media (max-width: 680px) {
    grid-template-columns: 32px 1fr 120px;
    .amount {
      grid-column: 2 / 4;
      text-align: left;
    }
  }
`;

const StickySummary = styled.aside`
  position: sticky;
  top: 1rem;
  border: 1px solid #e6ebf3;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 28, 51, 0.04);
  overflow: hidden;

  .summary-top {
    padding: 0.95rem 1.1rem;
    border-bottom: 1px solid #eef2f7;
    background: #fff;
  }

  .label {
    color: #7a8699;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 700;
  }

  .total {
    color: #0f1c33;
    font-size: 1.45rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.1;
    margin-top: 0.18rem;
  }

  .summary-body {
    padding: 0.85rem 1.1rem 1rem;
  }

  .summary-row {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.5rem 0;
    border-bottom: 1px solid #f1f5f9;
    color: #475569;
    font-size: 0.88rem;
  }

  .summary-row strong {
    color: #0f1c33;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  .summary-row:last-child {
    border-bottom: 0;
  }

  @media (max-width: 980px) {
    position: static;
  }
`;

const ClientTermsScale = styled.div`
  display: flex;
  justify-content: space-between;
  color: #7a8699;
  font-size: 0.78rem;
  font-weight: 700;
  margin-top: 0.25rem;
`;

const StepRail = styled.div`
  display: grid;
  gap: 0.3rem;
  margin-bottom: 0.85rem;
`;

const StepRailButton = styled.button`
  display: grid;
  grid-template-columns: 20px 1fr auto;
  align-items: center;
  gap: 0.55rem;
  width: 100%;
  border: 1px solid
    ${({ $active, $completed }) => {
      if ($active) return '#c7d2fe';
      if ($completed) return '#d1fae5';
      return '#e6ebf3';
    }};
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  background: ${({ $active }) => ($active ? '#f5f7ff' : '#ffffff')};
  color: #0f1c33;
  font-size: 0.85rem;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #c7d2fe;
  }

  .pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 999px;
    background: ${({ $active, $completed }) => {
      if ($completed) return '#16a34a';
      if ($active) return '#4f46e5';
      return '#e2e8f0';
    }};
    color: ${({ $active, $completed }) =>
      $active || $completed ? '#ffffff' : '#475569'};
    font-size: 0.65rem;
    font-weight: 700;
  }

  .step-value {
    color: #6b7689;
    font-size: 0.78rem;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
`;

const PaymentPlanSetupPage = () => {
  const { claimId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, hasPermission } = useUser();
  const isAdmin = user?.userType === 'admin' || hasPermission('billing:manage');
  const returnPath = isAdmin
    ? `/admin/profile/${claimId}?tab=billing`
    : '/dashboard?tab=billing';
  const confirmationPath = `/billing/payment-plan/${claimId}/confirmation`;
  const paymentPlanSessionId = searchParams.get('payment_plan_session');
  const {
    data: summary,
    isLoading,
    isFetching,
  } = useFetchPaymentPlanSummary(claimId, { enabled: !!claimId });

  const todayRef = useRef(null);
  const scheduleRef = useRef(null);
  const reviewRef = useRef(null);
  const [activeStep, setActiveStep] = useState('today');
  const stepIndex = STEP_ORDER.indexOf(activeStep);
  const isStepCompleted = (step) => STEP_ORDER.indexOf(step) < stepIndex;

  // Per-invoice plans: setup is scoped to ONE invoice. The invoice arrives in
  // the URL (?invoiceId=..., set by every entry point: invoice-card buttons,
  // offer emails, reminder banners); without one, a single eligible invoice
  // defaults and 2+ eligible renders the chooser.
  const requestedInvoiceId = searchParams.get('invoiceId');
  const plans = summary?.plans || [];
  const eligibleInvoices = summary?.balance?.eligibleInvoices || [];
  const planCovers = (plan, invoiceId) =>
    (plan?.sourceInvoiceIds || []).includes(invoiceId);

  const selectedEligibleInvoice = requestedInvoiceId
    ? eligibleInvoices.find((inv) => inv.invoiceId === requestedInvoiceId) ||
      null
    : eligibleInvoices.length === 1
      ? eligibleInvoices[0]
      : null;

  // The setup_pending plan FOR THIS invoice (edit mode). Without an invoiceId
  // a single setup_pending plan defaults (old deep links keep working).
  const setupPendingPlans = plans.filter(
    (plan) => plan.status === 'setup_pending',
  );
  const existingPlan = requestedInvoiceId
    ? setupPendingPlans.find((plan) => planCovers(plan, requestedInvoiceId)) ||
      null
    : setupPendingPlans.length === 1
      ? setupPendingPlans[0]
      : null;
  const existingPlanDepositProcessing =
    isPaymentPlanDepositProcessing(existingPlan);
  const isEditingSetup = Boolean(existingPlan?.paymentPlanId);
  const selectedInvoiceId = isEditingSetup
    ? (existingPlan.sourceInvoiceIds || [])[0] || requestedInvoiceId || null
    : selectedEligibleInvoice?.invoiceId || null;
  const selectedInvoiceLabel = isEditingSetup
    ? existingPlan?.sourceInvoice?.label || null
    : selectedEligibleInvoice
      ? `${formatCents(selectedEligibleInvoice.invoiceAmountCents)} invoice · invoiced ${dayjs(selectedEligibleInvoice.invoiceDate).format('MMM D, YYYY')}`
      : null;
  const needsInvoiceChoice =
    !isEditingSetup && !selectedEligibleInvoice && eligibleInvoices.length > 1;
  const balanceCents = isEditingSetup
    ? Number(existingPlan?.totalCents || 0)
    : Number(selectedEligibleInvoice?.invoiceAmountCents || 0);
  const hasBalance = balanceCents > 0;
  const savedPaymentMethods = summary?.savedPaymentMethods || [];
  const defaults = summary?.defaults || {};
  const minTerm = isAdmin ? defaults.minTermMonths || 1 : 1;
  const maxTerm = isAdmin ? defaults.maxTermMonths || 24 : 6;
  const clientTerms = defaults.clientTerms?.length
    ? defaults.clientTerms
    : [1, 2, 3, 4, 5, 6];
  const billingDate =
    summary?.balance?.asOfDate && dayjs(summary.balance.asOfDate).isValid()
      ? dayjs(summary.balance.asOfDate)
      : dayjs(existingPlan?.installments?.[0]?.dueDate || undefined);
  const minimumStartDate = billingDate.format('YYYY-MM-DD');
  // Staff get the extended 3-month window; edit mode also grandfathers a
  // stored first-installment date beyond the viewer's own window (matches
  // the server's allowThroughDate rule).
  const windowMaxDate = getLatestFirstInstallmentDate(billingDate, {
    extendedWindow: isAdmin,
  });
  const storedFirstInstallmentDueDate = (existingPlan?.installments || []).find(
    (installment) => Number(installment.sequence) === 1,
  )?.dueDate;
  const latestFirstInstallmentDate =
    storedFirstInstallmentDueDate &&
    dayjs(storedFirstInstallmentDueDate).isAfter(dayjs(windowMaxDate))
      ? dayjs(storedFirstInstallmentDueDate).format('YYYY-MM-DD')
      : windowMaxDate;
  const defaultFirstInstallmentDate =
    getDefaultFirstInstallmentDate(billingDate);

  const [termMonths, setTermMonths] = useState(isAdmin ? 3 : 3);
  const [downPaymentCents, setDownPaymentCents] = useState(0);
  const [downPaymentInput, setDownPaymentInput] = useState('0.00');
  const [firstInstallmentDate, setFirstInstallmentDate] = useState(
    defaultFirstInstallmentDate,
  );
  const [customInstallmentDates, setCustomInstallmentDates] = useState([]);
  const [note, setNote] = useState('');
  const [authorizationAcknowledgments, setAuthorizationAcknowledgments] =
    useState(() => createPaymentPlanAuthorizationState());
  const [typedSignature, setTypedSignature] = useState('');
  // Card-reuse shortcut: 'new' = normal Stripe Checkout; a plan id = charge
  // the deposit off-session on that plan's saved card (client self-serve
  // only — staff-proposed plans still collect via the client's checkout).
  const [paymentMethodChoice, setPaymentMethodChoice] = useState('new');
  const signerName = getPaymentPlanAuthorizationSignerName(user);

  useEffect(() => {
    if (!summary) return;
    const futureInstallments = (existingPlan?.installments || [])
      .filter((installment) => Number(installment.sequence) > 0)
      .sort((a, b) => Number(a.sequence) - Number(b.sequence));
    const existingFirstDate = futureInstallments[0]?.dueDate;
    const defaultDownPaymentCents = isEditingSetup
      ? Number(existingPlan?.downPaymentCents || 0)
      : Math.round((balanceCents * (defaults.downPaymentPercent || 10)) / 100);
    const nextTerm = clamp(
      Number(existingPlan?.termMonths || 3),
      minTerm,
      maxTerm,
    );
    const nextFirstInstallmentDate =
      existingFirstDate || defaultFirstInstallmentDate;

    setTermMonths(nextTerm);
    setDownPaymentCents(defaultDownPaymentCents);
    setDownPaymentInput((defaultDownPaymentCents / 100).toFixed(2));
    setFirstInstallmentDate(nextFirstInstallmentDate);
    setCustomInstallmentDates(
      futureInstallments.length
        ? futureInstallments.map((installment) => installment.dueDate)
        : Array.from({ length: nextTerm }, (_, index) =>
            dayjs(nextFirstInstallmentDate)
              .add(index, 'month')
              .format('YYYY-MM-DD'),
          ),
    );
    setNote('');
    setAuthorizationAcknowledgments(createPaymentPlanAuthorizationState());
    setTypedSignature(signerName);
  }, [
    summary,
    existingPlan,
    isEditingSetup,
    balanceCents,
    defaults.downPaymentPercent,
    defaultFirstInstallmentDate,
    minTerm,
    maxTerm,
    signerName,
  ]);

  const adjustedTerm = clamp(termMonths, minTerm, maxTerm);
  // Blocked per-invoice: only a plan covering the SELECTED invoice blocks its
  // setup — sibling plans on other invoices never do.
  const coveringPlan = selectedInvoiceId
    ? plans.find((plan) => planCovers(plan, selectedInvoiceId)) || null
    : null;
  const currentPlanBlocksSetup =
    coveringPlan &&
    (coveringPlan.status !== 'setup_pending' || existingPlanDepositProcessing);

  useEffect(() => {
    if (!paymentPlanSessionId || !summary?.currentPlan) return;
    const planParam = searchParams.get('plan');
    navigate(
      `${confirmationPath}?payment_plan_session=${paymentPlanSessionId}${planParam ? `&plan=${planParam}` : ''}`,
      { replace: true },
    );
  }, [
    confirmationPath,
    navigate,
    paymentPlanSessionId,
    searchParams,
    summary?.currentPlan,
  ]);

  useEffect(() => {
    if (!isAdmin) return;
    setCustomInstallmentDates((previousDates) =>
      Array.from(
        { length: adjustedTerm },
        (_, index) =>
          previousDates[index] ||
          dayjs(firstInstallmentDate).add(index, 'month').format('YYYY-MM-DD'),
      ),
    );
  }, [isAdmin, adjustedTerm, firstInstallmentDate]);

  useEffect(() => {
    const sections = [
      { ref: todayRef, key: 'today' },
      { ref: scheduleRef, key: 'schedule' },
      { ref: reviewRef, key: 'review' },
    ];
    const elements = sections.filter((entry) => entry.ref.current);
    if (!elements.length || typeof IntersectionObserver === 'undefined')
      return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting);
        if (!visible.length) return;
        const topmost = visible.reduce((accumulator, entry) =>
          accumulator.boundingClientRect.top < entry.boundingClientRect.top
            ? accumulator
            : entry,
        );
        const matched = elements.find(
          (entry) => entry.ref.current === topmost.target,
        );
        if (matched) setActiveStep(matched.key);
      },
      { rootMargin: '-30% 0px -55% 0px' },
    );
    elements.forEach((entry) => observer.observe(entry.ref.current));
    return () => observer.disconnect();
  }, []);

  const installmentDueDatesForSchedule = useMemo(() => {
    if (!isAdmin) return undefined;
    return Array.from(
      { length: adjustedTerm },
      (_, index) =>
        customInstallmentDates[index] ||
        dayjs(firstInstallmentDate).add(index, 'month').format('YYYY-MM-DD'),
    );
  }, [isAdmin, adjustedTerm, customInstallmentDates, firstInstallmentDate]);

  const schedule = useMemo(
    () =>
      computeSchedule({
        balanceCents,
        termMonths: adjustedTerm,
        downPaymentCents,
        firstInstallmentDate,
        installmentDueDates: installmentDueDatesForSchedule,
      }),
    [
      balanceCents,
      adjustedTerm,
      downPaymentCents,
      firstInstallmentDate,
      installmentDueDatesForSchedule,
    ],
  );

  const startPlan = useStartPaymentPlan(claimId, (result) => {
    if (result?.usedSavedPaymentMethod) {
      // Saved-card path: the deposit charged off-session and the plan is
      // already ACTIVE — no Stripe redirect, straight to confirmation.
      toast.success('Payment plan activated using your saved card.');
      navigate(`${confirmationPath}?plan=${result?.plan?.paymentPlanId || ''}`);
      return;
    }
    if (isAdmin) {
      toast.success(
        'Payment plan set up. The client can complete setup from their billing tab.',
      );
      navigate(returnPath);
      return;
    }
    toast.success('Opening Stripe checkout.');
    openCheckoutUrl(result?.checkoutSession?.url);
    navigate(returnPath);
  });

  const updatePlanSetup = useUpdatePaymentPlanSetup(claimId, (result) => {
    if (!isAdmin && result?.checkoutSession?.url) {
      toast.success('Payment plan terms updated. Opening Stripe checkout.');
      openCheckoutUrl(result.checkoutSession.url);
      navigate(returnPath);
      return;
    }
    toast.success('Payment plan terms updated.');
    navigate(returnPath);
  });

  const scrollTo = (ref, step) => {
    setActiveStep(step);
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const setDownPaymentFromCents = (cents) => {
    const clampedCents = clamp(Math.round(cents || 0), 0, balanceCents);
    setDownPaymentCents(clampedCents);
    setDownPaymentInput((clampedCents / 100).toFixed(2));
  };

  const handleDownPaymentInputChange = (event) => {
    const next = event.target.value;
    setDownPaymentInput(next);
    const dollars = Number(next);
    if (!Number.isFinite(dollars) || dollars < 0) return;
    setDownPaymentCents(clamp(Math.round(dollars * 100), 0, balanceCents));
  };

  const handleFirstInstallmentDateChange = (nextDate) => {
    setFirstInstallmentDate(nextDate);
    if (!isAdmin || !nextDate) return;
    setCustomInstallmentDates(
      Array.from({ length: adjustedTerm }, (_, index) =>
        dayjs(nextDate).add(index, 'month').format('YYYY-MM-DD'),
      ),
    );
  };

  const handleCustomInstallmentDateChange = (index, nextDate) => {
    setCustomInstallmentDates((previousDates) =>
      Array.from({ length: adjustedTerm }, (_, currentIndex) =>
        currentIndex === index
          ? nextDate
          : previousDates[currentIndex] ||
            dayjs(firstInstallmentDate)
              .add(currentIndex, 'month')
              .format('YYYY-MM-DD'),
      ),
    );
    if (index === 0) setFirstInstallmentDate(nextDate);
  };

  const payload = () => ({
    invoiceId: selectedInvoiceId || undefined,
    termMonths: adjustedTerm,
    downPaymentAmount: (downPaymentCents / 100).toFixed(2),
    firstInstallmentDate,
    installmentDueDates: installmentDueDatesForSchedule,
    note: note?.trim() ? note.trim() : undefined,
    returnPath: confirmationPath,
    paymentPlanAuthorization: isAdmin
      ? undefined
      : buildPaymentPlanAuthorizationPayload({
          acknowledgments: authorizationAcknowledgments,
          typedSignature,
          schedule,
          supportContact: summary?.supportContact,
        }),
    paymentMethodSource:
      !isAdmin && !isEditingSetup && paymentMethodChoice !== 'new'
        ? { type: 'saved', sourcePaymentPlanId: paymentMethodChoice }
        : undefined,
  });

  const handleSubmit = () => {
    if (existingPlanDepositProcessing) {
      toast.error('Payment plan checkout is already processing.');
      return;
    }
    if (
      !isAdmin &&
      !isPaymentPlanAuthorizationComplete(
        authorizationAcknowledgments,
        typedSignature,
      )
    ) {
      toast.error(
        'Review and accept the payment plan authorization before continuing.',
      );
      return;
    }
    const mutation = isEditingSetup ? updatePlanSetup : startPlan;
    const body = isEditingSetup
      ? { paymentPlanId: existingPlan.paymentPlanId, ...payload() }
      : payload();
    mutation.mutate(body, {
      onError: (err) => {
        // Saved-card failures fall back to the normal Checkout path: the
        // plan request survives a decline server-side, so a re-submit with
        // "Use a different card" goes straight through.
        const code = err?.response?.data?.code;
        if (
          code === 'saved_card_declined' ||
          code === 'saved_method_unavailable'
        ) {
          setPaymentMethodChoice('new');
          toast.error(
            `${err?.response?.data?.msg || 'The saved card could not be used.'} Submit again to use a different card.`,
          );
          return;
        }
        toast.error(
          mutationErrorMessage(
            err,
            isEditingSetup
              ? 'Failed to update payment plan terms.'
              : 'Failed to start payment plan setup.',
          ),
        );
      },
    });
  };

  if (isLoading && !summary) {
    return (
      <PrivateLayout>
        <Loading />
      </PrivateLayout>
    );
  }

  const depositPercent =
    balanceCents > 0 ? Math.round((downPaymentCents / balanceCents) * 100) : 0;
  const isZeroDownPayment = downPaymentCents === 0;
  const authorizationComplete =
    isAdmin ||
    isPaymentPlanAuthorizationComplete(
      authorizationAcknowledgments,
      typedSignature,
    );
  const firstInstallmentLabel = firstInstallmentDate
    ? dayjs(firstInstallmentDate).format('MMM D, YYYY')
    : 'Select date';
  const handleAuthorizationAcknowledgmentChange = (key, checked) => {
    setAuthorizationAcknowledgments((previous) => ({
      ...previous,
      [key]: checked,
    }));
  };
  const submitting = startPlan.isLoading || updatePlanSetup.isLoading;

  return (
    <PrivateLayout>
      <PageShell>
        <WizardFrame>
          <TopBar>
            <div>
              <h1>
                {isEditingSetup
                  ? 'Adjust the payment schedule'
                  : 'Set up a payment plan'}
              </h1>
              {selectedInvoiceLabel ? (
                // Per-invoice plans: every step of setup names the invoice
                // this plan covers (never in question — Madison, 2026-07-30).
                <p
                  style={{
                    margin: '0.2rem 0 0.2rem',
                    fontWeight: 700,
                    color: '#4f46e5',
                  }}
                >
                  For the {selectedInvoiceLabel}
                </p>
              ) : null}
              <p>
                {isAdmin
                  ? 'Build the plan here. The client activates it from their billing tab by completing Stripe setup.'
                  : 'Choose today’s checkout amount, then set when future installments begin.'}
              </p>
            </div>
            <Button
              variant="outline-secondary"
              size="sm"
              className="back-btn"
              onClick={() => navigate(returnPath)}
            >
              <ArrowLeft size={14} className="me-1" />
              Back to billing
            </Button>
          </TopBar>

          {needsInvoiceChoice ? (
            // Fallback chooser: only reachable from links without an
            // invoiceId while 2+ invoices are eligible. Explicit choice —
            // never a pre-selected combined anything.
            <Alert
              variant="light"
              style={{ background: '#fff', border: '1px dashed #d9e0ea' }}
            >
              <strong>Which invoice is this plan for?</strong>
              <p style={{ margin: '0.4rem 0 0.75rem', color: '#6b7689' }}>
                Each payment plan covers exactly one invoice. Pick the invoice
                to set this plan up for.
              </p>
              {eligibleInvoices.map((inv) => (
                <div
                  key={inv.invoiceId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '0.5rem 0',
                    borderTop: '1px solid #eef1f6',
                  }}
                >
                  <span style={{ fontWeight: 700 }}>
                    {formatCents(inv.invoiceAmountCents)}
                  </span>
                  <span style={{ color: '#7a8699', fontSize: '0.9rem' }}>
                    invoiced {dayjs(inv.invoiceDate).format('MMM D, YYYY')}
                  </span>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    style={{ marginLeft: 'auto' }}
                    onClick={() =>
                      navigate(
                        `/billing/payment-plan/${claimId}/setup?invoiceId=${inv.invoiceId}`,
                        { replace: true },
                      )
                    }
                  >
                    Set up a plan for this invoice
                  </Button>
                </div>
              ))}
            </Alert>
          ) : !hasBalance || currentPlanBlocksSetup ? (
            <Alert
              variant="light"
              style={{ background: '#fff', border: '1px dashed #d9e0ea' }}
            >
              {existingPlanDepositProcessing ? (
                <>
                  <strong>Bank payment processing.</strong> The down payment was
                  submitted through Stripe. ACH payments can take a few business
                  days to confirm, and the plan cannot be changed while it is
                  processing.
                </>
              ) : currentPlanBlocksSetup ? (
                <>
                  <strong>This invoice already has a payment plan.</strong>{' '}
                  Return to billing to review it.
                </>
              ) : (
                <>
                  <strong>No eligible balance.</strong>{' '}
                  {isAdmin
                    ? 'This invoice does not qualify for a payment plan right now.'
                    : 'This invoice does not qualify for a payment plan right now. If you believe this is incorrect, please contact us from your billing page.'}
                </>
              )}
            </Alert>
          ) : (
            <WizardGrid>
              <StepStack>
                <WizardSection ref={todayRef} data-variant="today">
                  <SectionHeader
                    $variant="today"
                    $completed={isStepCompleted('today')}
                  >
                    <div>
                      <div className="step-kicker">
                        <span className="step-num">
                          {isStepCompleted('today') ? (
                            <CheckCircle2 size={12} />
                          ) : (
                            '1'
                          )}
                        </span>
                        <Clock3 size={12} />
                        {isZeroDownPayment
                          ? 'Save the billing method today'
                          : 'Today’s down payment'}
                      </div>
                      {/* <h2>{isZeroDownPayment ? 'Save the billing method today' : 'Today’s down payment'}</h2> */}
                    </div>
                    <div className="section-aside">
                      <span>
                        {isZeroDownPayment
                          ? 'Today'
                          : `${depositPercent}% down`}
                      </span>
                      {formatCents(schedule?.downPaymentCents || 0)}
                    </div>
                  </SectionHeader>
                  <SectionBody>
                    <ControlBlock>
                      <div className="control-label">
                        <span className="label">Amount</span>
                        <span className="value">{depositPercent}%</span>
                      </div>
                      <InlineMoneyEditor>
                        <span>$</span>
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          max={balanceCents / 100}
                          value={downPaymentInput}
                          onChange={handleDownPaymentInputChange}
                          onBlur={() =>
                            setDownPaymentFromCents(downPaymentCents)
                          }
                          aria-label="Down payment amount charged today"
                        />
                      </InlineMoneyEditor>
                      <ChipRow>
                        <Chip
                          type="button"
                          $active={isZeroDownPayment}
                          onClick={() => setDownPaymentFromCents(0)}
                        >
                          0%
                        </Chip>
                        {PRESET_DEPOSIT_PERCENTS.map((pct) => (
                          <Chip
                            key={pct}
                            type="button"
                            $active={depositPercent === pct}
                            onClick={() =>
                              setDownPaymentFromCents(
                                Math.round((balanceCents * pct) / 100),
                              )
                            }
                          >
                            {pct}%
                          </Chip>
                        ))}
                      </ChipRow>
                    </ControlBlock>
                    <HintPill $variant="today">
                      <CreditCard size={14} />
                      {isZeroDownPayment
                        ? 'Stripe saves the billing method today - no charge now.'
                        : `Charged immediately at Stripe checkout — ${formatCents(schedule?.downPaymentCents || 0)} today.`}
                    </HintPill>
                  </SectionBody>
                </WizardSection>

                <WizardSection ref={scheduleRef}>
                  <SectionHeader $completed={isStepCompleted('schedule')}>
                    <div>
                      <div className="step-kicker">
                        <span className="step-num">
                          {isStepCompleted('schedule') ? (
                            <CheckCircle2 size={12} />
                          ) : (
                            '2'
                          )}
                        </span>
                        <CalendarDays size={12} />
                        Schedule
                      </div>
                    </div>
                    <div className="section-aside">
                      <span>{adjustedTerm} payments</span>
                      {formatCents(schedule?.monthlyTopCents || 0)}
                    </div>
                  </SectionHeader>
                  <SectionBody>
                    <ControlGrid>
                      <ControlBlock>
                        <div className="control-label">
                          <span className="label">Term</span>
                          <span className="value">{adjustedTerm} mo</span>
                        </div>
                        {isAdmin ? (
                          <ChipRow>
                            {PRESET_TERMS.map((term) => (
                              <Chip
                                key={term}
                                type="button"
                                $active={adjustedTerm === term}
                                onClick={() => setTermMonths(term)}
                              >
                                {term} mo
                              </Chip>
                            ))}
                          </ChipRow>
                        ) : null}
                        <input
                          type="range"
                          min={minTerm}
                          max={maxTerm}
                          step={1}
                          value={adjustedTerm}
                          onChange={(event) =>
                            setTermMonths(Number(event.target.value))
                          }
                        />
                        {!isAdmin ? (
                          <ClientTermsScale>
                            {clientTerms.map((term) => (
                              <span key={term}>{term}</span>
                            ))}
                          </ClientTermsScale>
                        ) : null}
                      </ControlBlock>

                      <ControlBlock>
                        <div className="control-label">
                          <span className="label">First Installment Date</span>
                          <span className="value">{firstInstallmentLabel}</span>
                        </div>
                        <Form.Control
                          type="date"
                          min={minimumStartDate}
                          max={latestFirstInstallmentDate}
                          value={firstInstallmentDate}
                          onChange={(event) =>
                            handleFirstInstallmentDateChange(event.target.value)
                          }
                        />
                        <HintPill>
                          {isAdmin
                            ? `First installment can be any date through ${dayjs(windowMaxDate).format('MMMM D, YYYY')}.`
                            : latestFirstInstallmentDate !== windowMaxDate
                              ? `This plan's first installment is scheduled for ${dayjs(latestFirstInstallmentDate).format('MMMM D, YYYY')}. You can keep that date or choose an earlier one.`
                              : "First installment must be within 30 days or by the 1st of next month, whichever is later. If the next 1st is two weeks or less away, the following month's 1st is available too."}
                        </HintPill>
                      </ControlBlock>
                    </ControlGrid>
                  </SectionBody>
                </WizardSection>

                <WizardSection ref={reviewRef}>
                  <SectionHeader>
                    <div>
                      <div className="step-kicker">
                        <span className="step-num">3</span>
                        <CheckCircle2 size={12} />
                        Review
                      </div>
                    </div>
                    <div className="section-aside">
                      <span>Total</span>
                      {formatCents(schedule?.totalCents || 0)}
                    </div>
                  </SectionHeader>
                  <SectionBody>
                    <ScheduleList>
                      <ScheduleHeader>
                        <div>#</div>
                        <div>Payment</div>
                        <div>Due</div>
                        <div className="amount">Amount</div>
                      </ScheduleHeader>
                      <ScheduleRow $isDeposit>
                        <div className="num">
                          <Clock3 size={13} />
                        </div>
                        <div>
                          <div className="label">
                            {isZeroDownPayment
                              ? 'Billing method setup'
                              : 'Down payment'}
                          </div>
                          <div className="status">
                            {isZeroDownPayment
                              ? 'No charge today'
                              : 'Charged in Stripe checkout'}
                          </div>
                        </div>
                        <div className="date">Today</div>
                        <div className="amount">
                          {formatCents(schedule?.downPaymentCents || 0)}
                        </div>
                      </ScheduleRow>
                      {(schedule?.installments || []).map(
                        (installment, index) => (
                          <ScheduleRow key={index}>
                            <div className="num">{index + 1}</div>
                            <div>
                              <div className="label">
                                Installment {index + 1}
                              </div>
                              <div className="status">
                                Auto-charge to saved billing method
                              </div>
                            </div>
                            <div className="date">
                              {isAdmin ? (
                                <Form.Control
                                  size="sm"
                                  type="date"
                                  min={minimumStartDate}
                                  max={
                                    index === 0
                                      ? latestFirstInstallmentDate
                                      : undefined
                                  }
                                  value={
                                    installmentDueDatesForSchedule?.[index] ||
                                    installment.dueDate
                                  }
                                  onChange={(event) =>
                                    handleCustomInstallmentDateChange(
                                      index,
                                      event.target.value,
                                    )
                                  }
                                />
                              ) : (
                                dayjs(installment.dueDate).format('MMM D, YYYY')
                              )}
                            </div>
                            <div className="amount">
                              {formatCents(installment.amountCents)}
                            </div>
                          </ScheduleRow>
                        ),
                      )}
                    </ScheduleList>
                    {!isAdmin ? (
                      <PaymentPlanAuthorizationTerms
                        acknowledgments={authorizationAcknowledgments}
                        onAcknowledgmentChange={
                          handleAuthorizationAcknowledgmentChange
                        }
                        typedSignature={typedSignature}
                        onTypedSignatureChange={setTypedSignature}
                        schedule={schedule}
                        signerName={signerName}
                        signerEmail={user?.userEmail}
                        supportContact={summary?.supportContact}
                        disabled={submitting || isFetching}
                      />
                    ) : null}
                    {isAdmin ? (
                      <ManagerNoteBlock>
                        <div className="note-label">Client setup note</div>
                        <div className="note-hint">
                          Optional context shown with the payment plan setup.
                        </div>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={note}
                          onChange={(event) => setNote(event.target.value)}
                          placeholder="Add a short note for the client"
                        />
                      </ManagerNoteBlock>
                    ) : null}
                    {!isAdmin &&
                    !isEditingSetup &&
                    savedPaymentMethods.length ? (
                      // Card-reuse shortcut: a settled card saved by another
                      // of this client's plans can charge the down payment
                      // right here — no Stripe redirect. Any other choice
                      // (or a decline) goes through normal Checkout.
                      <div
                        style={{
                          border: '1px solid #e2e8f0',
                          borderRadius: 10,
                          padding: '0.8rem 1rem',
                          margin: '0.9rem 0 0',
                          background: '#f8fafc',
                        }}
                      >
                        <div style={{ fontWeight: 700, marginBottom: 6 }}>
                          How would you like to pay?
                        </div>
                        {savedPaymentMethods.map((method) => (
                          <Form.Check
                            key={method.sourcePaymentPlanId}
                            type="radio"
                            id={`pm-${method.sourcePaymentPlanId}`}
                            name="payment-method-choice"
                            checked={
                              paymentMethodChoice === method.sourcePaymentPlanId
                            }
                            onChange={() =>
                              setPaymentMethodChoice(method.sourcePaymentPlanId)
                            }
                            label={`Use ${String(method.brand || 'card').toUpperCase()} ending ${method.last4} (already on file)`}
                          />
                        ))}
                        <Form.Check
                          type="radio"
                          id="pm-new"
                          name="payment-method-choice"
                          checked={paymentMethodChoice === 'new'}
                          onChange={() => setPaymentMethodChoice('new')}
                          label="Use a different card or bank account (Stripe checkout)"
                        />
                      </div>
                    ) : null}
                    <SubmitRow>
                      <Button
                        variant="primary"
                        disabled={
                          !hasBalance ||
                          !schedule ||
                          !firstInstallmentDate ||
                          !authorizationComplete ||
                          submitting ||
                          isFetching
                        }
                        onClick={handleSubmit}
                      >
                        {submitting &&
                        !isAdmin &&
                        paymentMethodChoice !== 'new' ? (
                          // The saved-card path charges the real deposit in
                          // this request (a few seconds of Stripe round
                          // trips) — say so, or the wait reads as a hang.
                          <>
                            <Spinner
                              animation="border"
                              size="sm"
                              className="me-2"
                            />
                            {isZeroDownPayment
                              ? 'Activating your plan…'
                              : 'Charging your card…'}
                          </>
                        ) : isAdmin ? (
                          <>
                            <ShieldCheck size={14} className="me-1" />
                            {isEditingSetup ? 'Save terms' : 'Set up plan'}
                          </>
                        ) : (
                          <>
                            <CreditCard size={14} className="me-1" />
                            {isEditingSetup
                              ? 'Save terms'
                              : paymentMethodChoice !== 'new'
                                ? isZeroDownPayment
                                  ? 'Activate plan with saved card'
                                  : 'Pay down payment with saved card'
                                : isZeroDownPayment
                                  ? 'Continue to billing method setup'
                                  : 'Continue to down payment'}
                          </>
                        )}
                      </Button>
                    </SubmitRow>
                  </SectionBody>
                </WizardSection>
              </StepStack>

              <StickySummary>
                <div className="summary-top">
                  <div className="label">Plan balance</div>
                  <div className="total">{formatCents(balanceCents)}</div>
                </div>
                <div className="summary-body">
                  <StepRail>
                    <StepRailButton
                      type="button"
                      $active={activeStep === 'today'}
                      $completed={isStepCompleted('today')}
                      onClick={() => scrollTo(todayRef, 'today')}
                    >
                      <span className="pill">
                        {isStepCompleted('today') ? (
                          <CheckCircle2 size={12} />
                        ) : (
                          '1'
                        )}
                      </span>
                      <span>Today</span>
                      <span className="step-value">
                        {formatCents(schedule?.downPaymentCents || 0)}
                      </span>
                    </StepRailButton>
                    <StepRailButton
                      type="button"
                      $active={activeStep === 'schedule'}
                      $completed={isStepCompleted('schedule')}
                      onClick={() => scrollTo(scheduleRef, 'schedule')}
                    >
                      <span className="pill">
                        {isStepCompleted('schedule') ? (
                          <CheckCircle2 size={12} />
                        ) : (
                          '2'
                        )}
                      </span>
                      <span>Schedule</span>
                      <span className="step-value">
                        {`${adjustedTerm} mo · ${formatCents(schedule?.monthlyTopCents || 0)}`}
                      </span>
                    </StepRailButton>
                    <StepRailButton
                      type="button"
                      $active={activeStep === 'review'}
                      $completed={false}
                      onClick={() => scrollTo(reviewRef, 'review')}
                    >
                      <span className="pill">3</span>
                      <span>Review</span>
                      <span className="step-value">
                        {formatCents(schedule?.totalCents || 0)}
                      </span>
                    </StepRailButton>
                  </StepRail>
                  <div className="summary-row">
                    <span>Installment 1 starts</span>
                    <strong>{firstInstallmentLabel}</strong>
                  </div>
                  <div className="summary-row">
                    <span>After down payment</span>
                    <strong>
                      {formatCents(schedule?.remainingCents || 0)}
                    </strong>
                  </div>
                </div>
              </StickySummary>
            </WizardGrid>
          )}
        </WizardFrame>
      </PageShell>
    </PrivateLayout>
  );
};

export default PaymentPlanSetupPage;
